import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiBriefcase, FiUsers, FiMail, FiPhone } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    compName: '',
    compEmail: '',
    compPhone: '',
    compAddress: ''
  });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        // Update company
        await adminAPI.updateCompany(editingCompany.compID, formData);
      } else {
        // Create company
        await adminAPI.createCompany(formData);
      }
      // Refresh data
      await fetchCompanies();
      resetForm();
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error('Error saving company: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = (companyId) => {
    setConfirmDialog({ isOpen: true, id: companyId });
  };

  const confirmDelete = async () => {
    try {
      await adminAPI.deleteCompany(confirmDialog.id);
      await fetchCompanies();
      setConfirmDialog({ isOpen: false, id: null });
      toast.success('Company deleted successfully!');
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Error deleting company: ' + (error.response?.data?.error || error.message));
      setConfirmDialog({ isOpen: false, id: null });
    }
  };

  const resetForm = () => {
    setFormData({
      compName: '',
      compEmail: '',
      compPhone: '',
      compAddress: ''
    });
    setEditingCompany(null);
    setShowCreateModal(false);
  };

  const startEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      compName: company.compName,
      compEmail: company.compEmail || '',
      compPhone: company.compPhone || '',
      compAddress: company.compAddress || ''
    });
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Companies</h1>
          <p className="mt-2 text-gray-600">
            Manage client companies and their subscriptions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus className="h-4 w-4" />
          <span>Add Company</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <FiBriefcase className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Companies</p>
              <p className="text-2xl font-semibold text-gray-900">{companies.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg mr-3">
              <FiUsers className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {companies.reduce((sum, comp) => sum + (comp.userCount || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg mr-3">
              <FiUsers className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-2xl font-semibold text-gray-900">
                {companies.reduce((sum, comp) => sum + (comp.agentCount || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Companies List */}
      {companies.length === 0 ? (
        <div className="card text-center py-12">
          <FiBriefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Companies</h3>
          <p className="text-gray-600 mb-6">
            Add your first client company to get started
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Add Company
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {companies.map((company) => (
            <div key={company.compID} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg mr-4">
                    <FiBriefcase className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {company.compName}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Company ID: {company.compID}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEdit(company)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(company.compID)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {company.compEmail && (
                  <div className="flex items-center text-sm">
                    <FiMail className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">{company.compEmail}</span>
                  </div>
                )}
                {company.compPhone && (
                  <div className="flex items-center text-sm">
                    <FiPhone className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-600">{company.compPhone}</span>
                  </div>
                )}
                {company.compAddress && (
                  <div className="text-sm text-gray-600">
                    {company.compAddress}
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Users:</span>
                  <span className="font-medium text-gray-900">{company.userCount || 0}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Agents:</span>
                  <span className="font-medium text-gray-900">{company.agentCount || 0}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Created:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(company.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {editingCompany ? 'Edit Company' : 'Add Company'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Company Name *</label>
                <input
                  type="text"
                  value={formData.compName}
                  onChange={(e) => setFormData({...formData, compName: e.target.value})}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.compEmail}
                  onChange={(e) => setFormData({...formData, compEmail: e.target.value})}
                  className="input"
                />
              </div>

              <div>
                <label className="label">Phone</label>
                <input
                  type="tel"
                  value={formData.compPhone}
                  onChange={(e) => setFormData({...formData, compPhone: e.target.value})}
                  className="input"
                />
              </div>

              <div>
                <label className="label">Address</label>
                <textarea
                  value={formData.compAddress}
                  onChange={(e) => setFormData({...formData, compAddress: e.target.value})}
                  className="input"
                  rows="3"
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingCompany ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Company"
        message="Are you sure you want to delete this company? This will also delete all associated users and agents. This action cannot be undone."
      />
    </div>
  );
};

export default Companies;
